import { metricNames, metricDisplayValues } from '@linearb/linjs-common';
import _ from 'lodash';
import { FORMATTERS } from 'views/root/views/home/views/performance/views/CustomReport/consts';
import { getCalculatedValue } from '../../performance/views/CustomReport/custom-report.serivce';
import { shouldFetchTotalsFromBackend } from '../../performance/views/CustomReport/custom-report.util';
import { getMappedV3Data } from 'common/services/metrics.service';

export const dashboardMetrics = [
  metricNames.CYCLE_TIME,
  metricNames.TIME_TO_PR,
  metricNames.TIME_TO_REVIEW,
  metricNames.REVIEW_TIME,
  metricNames.TIME_TO_PROD,
  metricNames.PR_SIZE,
  metricNames.REVIEW_DEPTH,
  metricNames.NEW_WORK,
  metricNames.REFACTOR,
  metricNames.REWORK,
  metricNames.PR_MERGED_WITHOUT_REVIEW,
  metricNames.COMMIT_CODE_CHANGES,
  metricNames.COMMIT_COUNT,
  metricNames.PR_NEW,
  metricNames.PR_MERGED,
  metricNames.REVIEWS,
  metricNames.RELEASES_COUNT,
  metricNames.ACTIVE_DAYS,
  metricNames.DONE_BRANCHES,
  metricNames.ACTIVE_BRANCHES,
  metricNames.CONTRIBUTOR_CODING_DAYS
];

// metrics that need a pair of metrics to calculate the metric displayed
const metricsPairs = {
  [metricNames.PR_SIZE]: [metricNames.PR_MERGED],
  [metricNames.NEW_WORK]: [metricNames.COMMIT_CODE_CHANGES],
  [metricNames.REFACTOR]: [metricNames.COMMIT_CODE_CHANGES],
  [metricNames.REWORK]: [metricNames.COMMIT_CODE_CHANGES],
  [metricNames.CYCLE_TIME]: [
    metricNames.TIME_TO_PR,
    metricNames.TIME_TO_REVIEW,
    metricNames.REVIEW_TIME,
    metricNames.TIME_TO_PROD
  ]
};

export const getMetricsNamesToFetch = (metricsNames = []) => {
  const extraMetricsToFetch = [];
  metricsNames.map(metricName => {
    if (metricsPairs[metricName]) {
      extraMetricsToFetch.push(...metricsPairs[metricName]);
    }
  });
  return _.uniq([...metricsNames, ...extraMetricsToFetch]);
};

/**
 * Metrics that are capped off at 100%
 */
const METRICS_TO_NORMALIZE = [metricNames.PM_CFR];

/**
 * Caps off graph displayed value at 100% for metrics where this is required
 * @param {number} value
 * @param {string} metricName
 * @returns {number} result capped off at 100%
 */
export const normalizeGraphValue = (value, metricName) => {
  if (!METRICS_TO_NORMALIZE.includes(metricName)) {
    return value;
  }

  if (value > 100) {
    return 100;
  }

  return value;
};

export const getAggregatedMetrics = () => {
  return dashboardMetrics.filter(metricName => {
    return shouldFetchTotalsFromBackend(metricName);
  });
};

// returns the width of cycle time - percent
export const calculateCycleTimeWidth = numberOfMetricsWidgets => {
  if (numberOfMetricsWidgets === 2) {
    return 60;
  }
  if (numberOfMetricsWidgets === 3) {
    return 50;
  }
  if (numberOfMetricsWidgets === 4) {
    return 40;
  }
};

export const getWidgetSectionWidth = cycleTimeWidth => {
  return `${100 - Number(cycleTimeWidth)}%`;
};

export const getMetricsWidgetWidth = numberOfMetricsWidgets => {
  return `${Math.round((1 / numberOfMetricsWidgets) * 100)}%`;
};

export const getMetricsWidgetsWidths = numberOfMetricsWidgets => {
  const cycleTimeWidth = calculateCycleTimeWidth(numberOfMetricsWidgets);
  return {
    cycleTimeWidth,
    widgetSectionWidth: getWidgetSectionWidth(cycleTimeWidth),
    metricsWidgetWidth: getMetricsWidgetWidth(numberOfMetricsWidgets)
  };
};

export const getFormattedV3Data = mappedV3Data => {
  const formattedV3Data = {};
  // when done with customize table - delete this function
  const aggregatedMetrics = getAggregatedMetrics();
  aggregatedMetrics.forEach(metric => {
    const unitType = _.get(metricDisplayValues[metric], 'unitType');
    const metricValue = mappedV3Data[metric];
    formattedV3Data[metric] = unitType
      ? FORMATTERS[unitType].avgFormatter(metricValue)
      : '';
  });

  return formattedV3Data;
};

export const getFormattedAndCalculatedV3Data = (
  mappedV3Data = {},
  metricsNames = [],
  availableMetricsWidgets,
  metricsPerGranularityMapped = [],
  fixedDecimalPlaces
) => {
  const formattedV3Data = {};
  const V3DataCalculated = {};
  const metricsWidgetsByName = _.keyBy(
    availableMetricsWidgets,
    'metrics[0].name'
  );

  metricsNames.forEach(metricName => {
    const unitType = _.get(metricsWidgetsByName[metricName], 'unitType');
    const avgByMetric = _.get(
      metricsWidgetsByName[metricName],
      'metrics[0].avgBy'
    );
    const percentOff = _.get(
      metricsWidgetsByName[metricName],
      'metrics[0].percentOff'
    );

    const percentOffMetricName = _.get(percentOff, 'metrics[0].name');
    const avgOnlyWhenDataAvailable = _.get(
      metricsWidgetsByName[metricName],
      'avgOnlyWhenDataAvailable'
    );
    const metricAggregatedValue = mappedV3Data[metricName];

    let percentOffMetricValue = _.get(mappedV3Data, percentOffMetricName, 1);
    // Normalize `percentOffMetricValue` to avoid division by zero
    if (percentOffMetricValue === 0) {
      percentOffMetricValue = 1;
    }

    const percentOffValue =
      percentOff &&
      getCalculatedValue(
        percentOffMetricName,
        null,
        avgOnlyWhenDataAvailable,
        null,
        metricsPerGranularityMapped
      );

    // should use total metrics values to calculate display value
    const shouldUseMetricsAggregatedValues =
      shouldFetchTotalsFromBackend(metricName);

    // calculation for Aggregated metrics that the backend doesn't do (such as cfr)
    const getTotalsV3Value = () => {
      if (percentOff && percentOffMetricName) {
        const result = (metricAggregatedValue / percentOffMetricValue) * 100;
        return normalizeGraphValue(result, metricName);
      }

      return metricAggregatedValue;
    };

    if (unitType) {
      if (shouldUseMetricsAggregatedValues) {
        V3DataCalculated[metricName] = getTotalsV3Value();
      } else {
        const calculetedValue = getCalculatedValue(
          metricName,
          avgByMetric,
          avgOnlyWhenDataAvailable,
          percentOffValue,
          metricsPerGranularityMapped
        );
        V3DataCalculated[metricName] =
          calculetedValue === '-' ? 0 : calculetedValue;
      }

      formattedV3Data[metricName] = FORMATTERS[unitType].avgFormatter(
        V3DataCalculated[metricName],
        fixedDecimalPlaces
      );
    } else {
      formattedV3Data[metricName] = '';
      V3DataCalculated[metricName] = 0;
    }
  });

  return { formattedV3Data, V3DataCalculated };
};

export const widgetsMetricsNamesInitialState = [
  metricNames.PR_SIZE,
  metricNames.PR_MERGED,
  metricNames.RELEASES_COUNT
];

export const tableMetricsNamesInitialState = [
  metricNames.CYCLE_TIME,
  metricNames.PR_SIZE,
  metricNames.PR_MERGED,
  metricNames.RELEASES_COUNT
];

export const getDescription = (
  availableMetricsWidget,
  granularity = 'week'
) => {
  const unitType = _.get(availableMetricsWidget, 'unitType');
  const unitDescription = _.get(
    availableMetricsWidget,
    'unitDescription',
    ''
  ).replace('changes', 'Changes');

  return unitType === 'count'
    ? `${unitDescription} ${granularity}`
    : unitDescription;
};

export const getIncludedMetrics = availableMetricsWidgets =>
  availableMetricsWidgets.filter(metric => {
    const availableMetricsWidgetName = _.get(metric, 'metrics[0].name', '');
    return dashboardMetrics.includes(availableMetricsWidgetName);
  });

export const getMetricsPerGranularityDataMapped = MetricsPerGranularity => {
  return MetricsPerGranularity.map(v3DataResult =>
    getMappedV3Data({ v3DataResult })
  );
};

export const getMappedData = (
  AggregatedMetrics,
  metricsPerGranularity = [],
  teamId
) => {
  const metricsByGroup = _.keyBy(AggregatedMetrics, 'group');
  const v3DataResult = _.get(metricsByGroup, `[${teamId}].data[0]`, {});
  const mappedV3Data = getMappedV3Data({ v3DataResult });
  const metricsPerGranularityCurrentGroup = _.get(
    metricsPerGranularity.filter(({ group }) => group === teamId),
    '[0].data',
    []
  );
  const metricsPerGranularityMapped = getMetricsPerGranularityDataMapped(
    metricsPerGranularityCurrentGroup
  );

  return { mappedV3Data, metricsPerGranularityMapped };
};
